/**
 * Reusable voice-to-text mic button. Wraps `activeTranscriber.transcribeLive()`
 * (on-device STT, mobile invariant 5: capture + transcribe at the edge, send only
 * text) so any text field can gain dictation. Tap to start, tap again to stop; the
 * final transcript is delivered via `onTranscribe`.
 *
 * Self-contained: owns its recording state, surfaces a "Listening…" hint and any
 * STT error inline. On failure (permission off, unsupported device, no speech) it
 * shows a short note — the caller's field stays usable for typing. NEVER imports
 * @smartbox/* (mobile invariant 1). No raw style values.
 */
import { useEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Button, Text } from '../ui';
import { theme } from '../../theme';
import { activeTranscriber, SttError, type LiveTranscription } from '../../voice/transcribe';

interface MicButtonProps {
  /** Called with the trimmed final transcript when recognition completes. */
  onTranscribe: (text: string) => void;
  /** Called with the growing interim transcript while the user is still speaking. */
  onPartialTranscribe?: (text: string) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  /** Button label suffix shown next to the mic glyph when idle. Empty string = glyph only. */
  label?: string;
  accessibilityLabel?: string;
}

function messageFor(err: unknown): string {
  if (err instanceof SttError) {
    switch (err.code) {
      case 'stt_permission_denied':
        return 'Mic permission is off — enable it in Settings, or type instead.';
      case 'stt_unsupported':
        return 'Voice input is not available on this device — type instead.';
      case 'stt_no_speech_detected':
        return 'No speech detected — try again or type instead.';
      default:
        return `Voice input failed (${err.code}) — type instead.`;
    }
  }
  return 'Voice input failed — type instead.';
}

export function MicButton({
  onTranscribe,
  onPartialTranscribe,
  disabled,
  size = 'sm',
  label = 'Speak',
  accessibilityLabel,
}: MicButtonProps) {
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionRef = useRef<LiveTranscription | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    return () => {
      mounted.current = false;
      sessionRef.current?.abort();
    };
  }, []);

  const start = async () => {
    setError(null);
    try {
      const session = await activeTranscriber.transcribeLive(
        onPartialTranscribe ? { onPartial: onPartialTranscribe } : undefined,
      );
      sessionRef.current = session;
      setRecording(true);
      session.result
        .then((text) => {
          const trimmed = text.trim();
          if (trimmed && mounted.current) onTranscribe(trimmed);
        })
        .catch((err) => {
          if (err instanceof SttError && err.code === 'stt_aborted') return;
          if (mounted.current) setError(messageFor(err));
        })
        .finally(() => {
          sessionRef.current = null;
          if (mounted.current) setRecording(false);
        });
    } catch (err) {
      if (mounted.current) {
        setError(messageFor(err));
        setRecording(false);
      }
    }
  };

  const stop = () => sessionRef.current?.stop();

  return (
    <View style={styles.wrap}>
      <Button
        title={recording ? 'Stop' : label ? `🎤 ${label}` : '🎤'}
        variant={recording ? 'primary' : 'secondary'}
        size={size}
        fullWidth={false}
        disabled={disabled && !recording}
        onPress={recording ? stop : start}
        accessibilityLabel={accessibilityLabel ?? (recording ? 'Stop voice input' : 'Start voice input')}
      />
      {recording ? (
        <Text variant="small" color="secondary">
          Listening…
        </Text>
      ) : null}
      {error ? (
        <Text variant="small" color="danger" accessibilityRole="alert">
          {error}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: theme.spacing.xs, alignItems: 'flex-start' },
});
